import { Col, Row } from "antd";
import { get, set, uniq } from "lodash";
import { useTranslation } from "react-i18next";
import {
  MdOutlineAddRoad,
  MdOutlineAlternateEmail,
  MdOutlineCall,
  MdOutlinePinDrop,
  MdOutlineSouthAmerica,
} from "react-icons/md";
import Form from "shared/components/Form";
import FormField from "shared/components/FormField";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { NAME_SPACES } from "shared/locales/constants";
import { CONTACT_POINT_SYSTEM, SUBSCRIPTION_FORM_KEYS } from "utils/constants";

const Contact = ({
  setPercent,
  isFieldRequired,
  initialValues,
  bindField,
  polynesiecities: cities,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("ANSET_SUBSCRIPTION", { returnObjects: true });
  const BLOCK = t("ANSET_SUBSCRIPTION.ADDITIONAL_INFORMATION", {
    returnObjects: true,
  });

  const mailboxPath = `telecoms.contact.${CONTACT_POINT_SYSTEM.MAILBOX}.value`;
  const mailboxField = bindField(mailboxPath, {
    label: SUBSCRIPTION.FORM.MAILBOX.LABEL,
    required: isFieldRequired(mailboxPath),
    onBlur: () => setPercent(initialValues),
  });

  const postalZipPath = `telecoms.contact.${CONTACT_POINT_SYSTEM.POSTAL_ZIP_CODE}.value`;
  const postalZipField = bindField(postalZipPath, {
    label: SUBSCRIPTION.FORM.ZIP_CODE.LABEL,
    required: isFieldRequired(postalZipPath),
    onChange: (value) => {
      const match = cities.find(({ zipCode }) => zipCode === value);
      if (match) {
        set(
          initialValues,
          `telecoms.contact.${CONTACT_POINT_SYSTEM.POSTAL_CITY}.value`,
          match.city,
        );
      }
      setPercent(initialValues);
    },
  });

  const postalCityPath = `telecoms.contact.${CONTACT_POINT_SYSTEM.POSTAL_CITY}.value`;
  const postalCityField = bindField(postalCityPath, {
    label: SUBSCRIPTION.FORM.CITY.LABEL,
    required: isFieldRequired(postalCityPath),
    onChange: () => setPercent(initialValues),
  });

  const addressPath = `telecoms.contact.${CONTACT_POINT_SYSTEM.ADDRESS}.value`;
  const addressField = bindField(addressPath, {
    label: SUBSCRIPTION.FORM.ADDRESS.LABEL,
    required: isFieldRequired(addressPath),
    onBlur: () => setPercent(initialValues),
  });

  const complementPath = `telecoms.contact.${CONTACT_POINT_SYSTEM.COMPLEMENT}.value`;
  const complementField = bindField(complementPath, {
    label: SUBSCRIPTION.FORM.COMPLEMENT.LABEL,
    required: isFieldRequired(complementPath),
    onBlur: () => setPercent(initialValues),
  });

  const zipCodePath = `telecoms.contact.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`;
  const zipCodeField = bindField(zipCodePath, {
    label: SUBSCRIPTION.FORM.ZIP_CODE.LABEL,
    required: isFieldRequired(zipCodePath),
    onChange: (value) => {
      const match = cities.find(({ zipCode }) => zipCode === value);
      if (match) {
        set(
          initialValues,
          `telecoms.contact.${CONTACT_POINT_SYSTEM.CITY}.value`,
          match.city,
        );
      }
      setPercent(initialValues);
    },
  });

  const cityPath = `telecoms.contact.${CONTACT_POINT_SYSTEM.CITY}.value`;
  const cityField = bindField(cityPath, {
    label: SUBSCRIPTION.FORM.CITY.LABEL,
    required: isFieldRequired(cityPath),
    onChange: () => setPercent(initialValues),
  });

  const phonePath = `telecoms.contact.${CONTACT_POINT_SYSTEM.PHONE}.value`;
  const phoneField = bindField(phonePath, {
    label: BLOCK.TERMINAL.FIELDS.VINI.LABEL,
    required: isFieldRequired(phonePath),
    onBlur: () => setPercent(initialValues),
  });

  const emailPath = `telecoms.contact.${CONTACT_POINT_SYSTEM.EMAIL}.value`;
  const emailField = bindField(emailPath, {
    label: SUBSCRIPTION.FORM.EMAIL.LABEL,
    required: isFieldRequired(emailPath),
    onBlur: () => setPercent(initialValues),
  });

  const postalCityOptions = cities
    .filter(
      ({ zipCode }) =>
        zipCode === get(initialValues, postalZipPath),
    )
    .map(({ city }) => ({ label: city, value: city }));

  const cityOptions = cities
    .filter(
      ({ zipCode }) => zipCode === get(initialValues, zipCodePath),
    )
    .map(({ city }) => ({ label: city, value: city }));

  const zipOptions = uniq(cities.map(({ zipCode }) => zipCode)).map(
    (zipCode) => ({ label: zipCode, value: zipCode }),
  );

  return (
    <div
      className="subscription-form--wrapper"
      id={`${SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.CONTACT}-section`}
    >
      <Form
        type="vertical"
        title={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.CONTACT.TITLE}
      >
        <Row gutter={[16, 16]}>
          <Col lg={24} xs={24}>
            <Form type="highlight" title={SUBSCRIPTION.FORM.MAILBOX.TITLE}>
              <div className="grid--1">
                <FormField
                  path={mailboxField.path}
                  label={mailboxField.label}
                  required={mailboxField.required}
                  disabled={mailboxField.disabled}
                >
                  {(field) => (
                    <Input
                      label={mailboxField.label}
                      icon={<MdOutlinePinDrop size={16} />}
                      placeholder={SUBSCRIPTION.FORM.MAILBOX.PLACEHOLDER}
                      defaultValue={mailboxField.defaultValue}
                      {...field}
                      onChange={mailboxField.onChange}
                      onBlur={mailboxField.onBlur}
                      optional={!mailboxField.required}
                    />
                  )}
                </FormField>
                <FormField
                  path={postalZipField.path}
                  label={postalZipField.label}
                  required={postalZipField.required}
                  disabled={postalZipField.disabled}
                >
                  {(field) => (
                    <Select
                      icon={<MdOutlineAddRoad size={16} />}
                      label={postalZipField.label}
                      placeholder={SUBSCRIPTION.FORM.ZIP_CODE.PLACEHOLDER}
                      showSearch
                      theme="light"
                      options={zipOptions}
                      defaultValue={postalZipField.defaultValue}
                      {...field}
                      onChange={postalZipField.onChange}
                      optional={!postalZipField.required}
                    />
                  )}
                </FormField>
                <FormField
                  path={postalCityField.path}
                  label={postalCityField.label}
                  required={postalCityField.required}
                  disabled={postalCityField.disabled}
                >
                  {(field) => (
                    <Select
                      theme="light"
                      icon={<MdOutlineSouthAmerica size={16} />}
                      label={postalCityField.label}
                      placeholder={SUBSCRIPTION.FORM.CITY.PLACEHOLDER}
                      showSearch
                      options={postalCityOptions}
                      defaultValue={postalCityField.defaultValue}
                      value={get(initialValues, postalCityPath)}
                      {...field}
                      onChange={postalCityField.onChange}
                      optional={!postalCityField.required}
                    />
                  )}
                </FormField>
              </div>
            </Form>
          </Col>
          <Col lg={24} xs={24}>
            <Form type="highlight" title={SUBSCRIPTION.FORM.ADDRESS.TITLE}>
              <div className="grid--1">
                <FormField
                  path={addressField.path}
                  label={addressField.label}
                  required={addressField.required}
                  disabled={addressField.disabled}
                >
                  {(field) => (
                    <Input
                      label={addressField.label}
                      icon={<MdOutlinePinDrop size={16} />}
                      placeholder={SUBSCRIPTION.FORM.ADDRESS.PLACEHOLDER}
                      defaultValue={addressField.defaultValue}
                      {...field}
                      onChange={addressField.onChange}
                      onBlur={addressField.onBlur}
                    />
                  )}
                </FormField>
                <FormField
                  path={complementField.path}
                  label={complementField.label}
                  required={complementField.required}
                  disabled={complementField.disabled}
                >
                  {(field) => (
                    <Input
                      label={complementField.label}
                      icon={<MdOutlinePinDrop size={16} />}
                      placeholder={SUBSCRIPTION.FORM.COMPLEMENT.PLACEHOLDER}
                      defaultValue={complementField.defaultValue}
                      {...field}
                      onChange={complementField.onChange}
                      onBlur={complementField.onBlur}
                      optional={!complementField.required}
                    />
                  )}
                </FormField>
                <FormField
                  path={zipCodeField.path}
                  label={zipCodeField.label}
                  required={zipCodeField.required}
                  disabled={zipCodeField.disabled}
                >
                  {(field) => (
                    <Select
                      icon={<MdOutlineAddRoad size={16} />}
                      label={zipCodeField.label}
                      placeholder={SUBSCRIPTION.FORM.ZIP_CODE.PLACEHOLDER}
                      theme="light"
                      showSearch
                      options={zipOptions}
                      defaultValue={zipCodeField.defaultValue}
                      {...field}
                      onChange={zipCodeField.onChange}
                    />
                  )}
                </FormField>
                <FormField
                  path={cityField.path}
                  label={cityField.label}
                  required={cityField.required}
                  disabled={cityField.disabled}
                >
                  {(field) => (
                    <Select
                      icon={<MdOutlineSouthAmerica size={16} />}
                      label={cityField.label}
                      placeholder={SUBSCRIPTION.FORM.CITY.PLACEHOLDER}
                      theme="light"
                      showSearch
                      options={cityOptions}
                      defaultValue={cityField.defaultValue}
                      value={get(initialValues, cityPath)}
                      {...field}
                      onChange={cityField.onChange}
                    />
                  )}
                </FormField>
              </div>
            </Form>
          </Col>
          <Col lg={24} xs={24}>
            <Form type="highlight" title={"Coordonnées de contact"}>
              <div className="grid--1">
                <FormField
                  path={phoneField.path}
                  label={phoneField.label}
                  required={phoneField.required}
                  disabled={phoneField.disabled}
                >
                  {(field) => (
                    <Input
                      placeholder={BLOCK.TERMINAL.FIELDS.VINI.PLACEHOLDER}
                      label={phoneField.label}
                      icon={<MdOutlineCall size={16} />}
                      defaultValue={phoneField.defaultValue}
                      {...field}
                      onChange={phoneField.onChange}
                      onBlur={phoneField.onBlur}
                    />
                  )}
                </FormField>
                <FormField
                  path={emailField.path}
                  label={emailField.label}
                  required={emailField.required}
                  disabled={emailField.disabled}
                >
                  {(field) => (
                    <Input
                      label={emailField.label}
                      icon={<MdOutlineAlternateEmail size={16} />}
                      placeholder={SUBSCRIPTION.FORM.EMAIL.PLACEHOLDER}
                      defaultValue={emailField.defaultValue}
                      {...field}
                      onChange={emailField.onChange}
                      onBlur={emailField.onBlur}
                    />
                  )}
                </FormField>
              </div>
            </Form>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default Contact;
